import React, { useEffect, useMemo } from "react";
import styles from "./ShopMegaMenuLinks.module.scss";
import { Link } from "react-router-dom";
import classnames from "classnames";
import SpinningLoader from "@src/components/common/SpinningLoader";
import ShopMegaMenuPromoBanner from "./ShopMegaMenuPromoBanner";
import type { ProductCategory } from "@src/types/product";
import kebabCase from "lodash/kebabCase";
import { useShopMegaMenuStore } from "@src/state/shopMegaMenuState";

const MEGA_MENU_MANUFACTURER_COUNT = 8;
const MEGA_MENU_SPECIES_COUNT = 8;

interface ShopMegaMenuLinksProps {
  onLinkClicked?: () => void;
  maxDepth?: number;
}

function splitRootCategories(
  columns: ProductCategory[],
): [ProductCategory[], ProductCategory[]] {
  if (columns.length === 0) {
    return [[], []];
  }
  const midpoint = Math.ceil(columns.length / 2);
  return [columns.slice(0, midpoint), columns.slice(midpoint)];
}

export default function ShopMegaMenuLinks(props: ShopMegaMenuLinksProps) {
  const { onLinkClicked, maxDepth = 1 } = props;

  const productCategories = useShopMegaMenuStore((s) => s.productCategories);
  const manufacturers = useShopMegaMenuStore((s) => s.manufacturers);
  const speciesList = useShopMegaMenuStore((s) => s.species);
  const loading = useShopMegaMenuStore((s) => s.loading);
  const failures = useShopMegaMenuStore((s) => s.failures);
  const allEndpointsFailed = useShopMegaMenuStore((s) => s.allEndpointsFailed);
  const fetchMegaMenuData = useShopMegaMenuStore((s) => s.fetchMegaMenuData);

  const [categoriesCol1, categoriesCol2] = useMemo(
    () => splitRootCategories(productCategories),
    [productCategories],
  );

  useEffect(() => {
    void fetchMegaMenuData();
  }, [fetchMegaMenuData]);

  const megaMenuManufacturers = manufacturers.slice(
    0,
    MEGA_MENU_MANUFACTURER_COUNT,
  );
  const megaMenuSpecies = speciesList.slice(0, MEGA_MENU_SPECIES_COUNT);
  const isLoadingMegaMenu = loading;

  const showCategories = !failures.categories;
  const showManufacturers = !failures.manufacturers;
  const showSpecies = !failures.species;

  function handleComingSoonClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    window.alert("Coming Soon!");
    onLinkClicked?.();
  }

  const renderProductCategory = (
    productCategory: ProductCategory,
    level: number = 1,
  ) => {
    return (
      <>
        <Link
          id={`shop-category-${kebabCase(productCategory.name)}`}
          to={`/product-category/${productCategory.id}`}
          onClick={onLinkClicked}
        >
          <span className={styles[`level${level}`]}>
            {productCategory.name}
          </span>
        </Link>
        {level < maxDepth &&
          productCategory.subcategories &&
          productCategory.subcategories.map((subcategory) => (
            <React.Fragment key={`product-category-${subcategory.id}`}>
              {renderProductCategory(subcategory, level + 1)}
            </React.Fragment>
          ))}
      </>
    );
  };

  return (
    <div className={styles.root}>
      {isLoadingMegaMenu && <SpinningLoader />}

      <div className={styles.columns}>
        {allEndpointsFailed ? (
          <div
            className={classnames(styles.section, styles.menuLoadError)}
            role="alert"
          >
            <div className={styles.menuLoadErrorTitle}>
              Menu Could Not Be Loaded. Please refresh this page to continue.
            </div>
            <p className={styles.menuLoadErrorBody}>
              We are currently unable to retrieve the menu due to an API error.
              Refresh the page to try again. If you are still receiving this
              error, please wait a little while before trying again or{" "}
              <span className={styles.menuLoadErrorSupportTail}>
                <Link
                  to="/support"
                  onClick={() => onLinkClicked?.()}
                  className={styles.menuLoadErrorSupportLink}
                >
                  contact support
                </Link>
                .
              </span>
            </p>
          </div>
        ) : (
          <>
            {showCategories && (
              <>
                <div
                  className={classnames(styles.section, styles.categoryColumn)}
                >
                  <div className={styles.sectionTitle}>Shop by Category</div>
                  {categoriesCol1.map((productCategory) => (
                    <div key={`mega-cat-col1-${productCategory.id}`}>
                      {renderProductCategory(productCategory)}
                    </div>
                  ))}
                </div>

                <div
                  className={classnames(styles.section, styles.categoryColumn)}
                >
                  <div className={styles.columnHeadingSpacer} aria-hidden />
                  {categoriesCol2.map((productCategory) => (
                    <div key={`mega-cat-col2-${productCategory.id}`}>
                      {renderProductCategory(productCategory)}
                    </div>
                  ))}
                </div>
              </>
            )}

            {showManufacturers && (
              <div className={classnames(styles.section, styles.narrowColumn)}>
                <div className={styles.sectionTitle}>Manufacturers</div>
                {megaMenuManufacturers.map((manufacturer) => (
                  <Link
                    key={manufacturer.id}
                    id={`shop-manufacturer-${kebabCase(manufacturer.name)}`}
                    to="/"
                    onClick={handleComingSoonClick}
                    className={styles.sectionLink}
                  >
                    {manufacturer.name}
                  </Link>
                ))}
              </div>
            )}

            {showSpecies && (
              <div className={classnames(styles.section, styles.narrowColumn)}>
                <div className={styles.sectionTitle}>Animals</div>
                {megaMenuSpecies.map((animal) => (
                  <Link
                    key={animal.id}
                    id={`shop-animal-${kebabCase(animal.speciesName)}`}
                    to="/"
                    onClick={handleComingSoonClick}
                    className={styles.sectionLink}
                  >
                    {animal.speciesName}
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        <div className={classnames(styles.section, styles.narrowColumn)}>
          <div className={styles.sectionTitle}>Quick Links</div>
          <Link
            id="shop-quick-new-products"
            to="/"
            onClick={handleComingSoonClick}
            className={styles.quickLink}
          >
            New Products
          </Link>
          <Link
            id="shop-quick-past-purchases"
            to="/product-recommendations/past-purchases"
            onClick={onLinkClicked}
            className={styles.quickLink}
          >
            Previously Purchased Items
          </Link>
          <Link
            id="shop-quick-favorites"
            to="/favorites"
            onClick={onLinkClicked}
            className={styles.quickLink}
          >
            Favorite Products
          </Link>
          <Link
            id="shop-quick-vetget-promo"
            to="/"
            onClick={handleComingSoonClick}
            className={styles.quickLink}
          >
            New to VetGet Promotion
          </Link>
          <Link
            id="shop-quick-cash-back"
            to="/"
            onClick={handleComingSoonClick}
            className={styles.quickLink}
          >
            Cash Back
          </Link>
          <Link
            id="shop-quick-track-cash-back"
            to="/"
            onClick={handleComingSoonClick}
            className={styles.quickLink}
          >
            Track My Cash Back
          </Link>
          <Link
            id="shop-quick-connect-vendor"
            to="/account/distributors"
            onClick={onLinkClicked}
            className={styles.quickLink}
          >
            Connect a Vendor
          </Link>
          <Link
            id="shop-quick-account-settings"
            to="/account/general"
            onClick={onLinkClicked}
            className={styles.quickLink}
          >
            My Account/Settings
          </Link>
        </div>
      </div>

      <ShopMegaMenuPromoBanner onClaimOfferClick={onLinkClicked} />
    </div>
  );
}
